import React, { useState, useRef, useEffect } from "react";
import { cn } from "./utils";
import { ChevronDown } from "lucide-react";

export function Select({ value, onValueChange, children, ...props }) {
  const [open, setOpen] = useState(false);
  const selectRef = useRef(null);
  const [selectedLabel, setSelectedLabel] = useState(null);

  const allChildren = React.Children.toArray(children);
  const selectContentChild = allChildren.find((child) => child && child.type === SelectContent);

  // ✅ Build a string label for the currently-selected value
  useEffect(() => {
    if (!value || !selectContentChild) {
      setSelectedLabel(null);
      return;
    }

    // SelectContent wraps items inside extra divs, so we must unwrap recursively.
    const unwrapItems = (nodes) => {
      const out = [];
      React.Children.forEach(nodes, (c) => {
        if (!c) return;
        if (c.type === SelectItem) out.push(c);
        else if (c.props?.children) out.push(...unwrapItems(c.props.children));
      });
      return out;
    };

    const items = unwrapItems(selectContentChild.props.children);
    const selectedItem = items.find((item) => String(item.props.value) === String(value));

    if (!selectedItem) {
      setSelectedLabel(String(value));
      return;
    }

    // ✅ 1) Prefer explicit textValue (works for rich JSX children)
    if (selectedItem.props.textValue != null) {
      setSelectedLabel(String(selectedItem.props.textValue));
      return;
    }

    // ✅ 2) Fallback: plain text child
    const c = selectedItem.props.children;
    if (typeof c === "string" || typeof c === "number") {
      setSelectedLabel(String(c));
      return;
    }

    // ✅ 3) Never stringify React elements (that causes [object Object])
    setSelectedLabel(String(value));
  }, [value, selectContentChild]);

  // ✅ Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={selectRef} {...props}>
      {allChildren.map((child, index) => {
        if (child && child.type === SelectTrigger) {
          return React.cloneElement(child, {
            key: `trigger-${index}`,
            value,
            open,
            setOpen,
            onValueChange,
            selectedLabel,
          });
        }

        if (child && child.type === SelectContent) {
          return open
            ? React.cloneElement(child, {
                key: `content-${index}`,
                onValueChange,
                setOpen,
                currentValue: value,
              })
            : null;
        }

        return child;
      })}
    </div>
  );
}

export function SelectTrigger({
  className,
  value,
  open,
  setOpen,
  selectedLabel,
  children,
  ...props
}) {
  const selectValueChild = React.Children.toArray(children).find(
    (child) => child && child.type === SelectValue
  );
  const placeholder = selectValueChild?.props?.placeholder || "";

  const normalizedSelectedLabel = React.useMemo(() => {
    if (selectedLabel == null) return "";

    // if string/number -> fine
    if (typeof selectedLabel === "string" || typeof selectedLabel === "number") {
      return String(selectedLabel);
    }

    // if something like { label: "X" }
    if (typeof selectedLabel === "object" && selectedLabel.label) {
      return String(selectedLabel.label);
    }

    // if it's a React element, React can render it, but your trigger expects text
    // so fallback to empty and let placeholder show (or compute your label elsewhere)
    if (React.isValidElement(selectedLabel)) {
      return "";
    }

    // last resort
    try {
      return String(selectedLabel);
    } catch {
      return "";
    }
  }, [selectedLabel]);

  const displayText = normalizedSelectedLabel || placeholder;
  //console.log(selectedLabel)
  //console.log(normalizedSelectedLabel, "  ", placeholder  )
  //console.log(displayText)
  return (
    <button
      type="button"
      onClick={() => setOpen(!open)}
      className={cn(
        "flex w-full items-center justify-between gap-2 rounded-md border border-input bg-input-background px-3 py-2 text-sm h-9 transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props}
    >
      <span
        className={cn("min-w-0 flex-1 truncate", !value && "text-muted-foreground")}
        title={displayText}
      >
        {displayText}
      </span>
      <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
    </button>
  );
}


export function SelectContent({
  className,
  onValueChange,
  setOpen,
  currentValue,
  children,
  ...props
}) {
  // ✅ Build lookup: value -> label
  const items = React.Children.toArray(children)
    .filter((child) => child && child.type === SelectItem)
    .map((child) => {
      const v = String(child.props.value);

      // Prefer textValue. If missing and child is plain string, use it.
      let label = child.props.textValue;
      if (label == null) {
        const c = child.props.children;
        if (typeof c === "string" || typeof c === "number") label = String(c);
      }

      return { value: v, label: label != null ? String(label) : "" };
    });

  const selectedLabel =
    items.find((x) => x.value === String(currentValue))?.label || "";

  return (
    <div
      className={cn(
        "absolute z-50 mt-1 w-full min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md",
        className
      )}
      {...props}
    >
      <div className="p-1">
        {React.Children.map(children, (child) => {
          if (child && child.type === SelectItem) {
            return React.cloneElement(child, {
              onValueChange,
              setOpen,
              currentValue,
            });
          }
          return child;
        })}
      </div>

    </div>
  );
}


export function SelectValue({ placeholder, ...props }) {
  return <span {...props}>{placeholder}</span>;
}

export function SelectItem({
  className,
  value: itemValue,
  textValue,                
  onValueChange,
  setOpen,
  currentValue,
  children,
  ...props
}) {
  const isSelected = currentValue === itemValue;

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onValueChange) onValueChange(itemValue);
    if (setOpen) setOpen(false);
  };

  return (
    <div
      onClick={handleClick}
      data-value={String(itemValue)}
      data-textvalue={textValue ? String(textValue) : ""} 
      className={cn(
        "relative flex w-full cursor-pointer items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-none select-none hover:bg-accent hover:text-accent-foreground",
        isSelected && "bg-accent text-accent-foreground",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}


